/**
 * Pure helpers for the shared <AgentBuilder scope> component.
 *
 * Phase 1 of PR-A AgentBuilder shared-component refactor (2026-05-11).
 * These functions were previously co-located inside the two route files
 * (`routes/agents/builder.tsx` + `routes/sysadmin/catalog.agents.builder.tsx`).
 * The mechanical move preserves the EXACT export names so the C1 (model
 * brand → UUID resolution) and L1 (cold-page race) regression tests
 * continue to pass without modification.
 *
 * Pure / no React dependencies / safe to call from tests + adapters.
 */
import type {
  AgentAutonomy,
  AgentTrigger,
} from '@/lib/mock-agents-data';

// ─── Slugify ────────────────────────────────────────────────────────────────

/**
 * Turn a display name into the kebab-case slug the catalog API uses as
 * the immutable identity key. Pure / exported for tests.
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60);
}

// ─── Trigger mode <-> API triggers round-trip ──────────────────────────────

export function triggersToModes(triggers: {
  conversational?: boolean;
  scheduled?: { cron: string; tz?: string } | null;
  event?: { types: string[] } | null;
}): AgentTrigger[] {
  const modes: AgentTrigger[] = [];
  if (triggers.conversational !== false) modes.push('conversational');
  if (triggers.scheduled) modes.push('scheduled');
  if (triggers.event) modes.push('event');
  return modes.length > 0 ? modes : ['conversational'];
}

export function modesToTriggers(
  modes: AgentTrigger[],
  cron: string,
  eventTypes: string[],
): {
  conversational: boolean;
  scheduled: { cron: string } | null;
  event: { types: string[] } | null;
} {
  return {
    conversational: modes.includes('conversational'),
    scheduled: modes.includes('scheduled') && cron.trim() ? { cron: cron.trim() } : null,
    event: modes.includes('event') && eventTypes.length > 0 ? { types: eventTypes } : null,
  };
}

// ─── Autonomy round-trip ────────────────────────────────────────────────────

export function mapAutonomyToApi(level: AgentAutonomy): 'read_only' | 'suggest' | 'auto_approve' {
  switch (level) {
    case 'auto_execute': return 'auto_approve';
    case 'approval_required': return 'suggest';
    case 'assisted': return 'suggest';
    case 'read_only': return 'read_only';
  }
}

export function mapAutonomyFromApi(
  level: 'read_only' | 'suggest' | 'auto_approve' | undefined,
): AgentAutonomy {
  switch (level) {
    case 'auto_approve': return 'auto_execute';
    case 'suggest': return 'assisted';
    case 'read_only': return 'read_only';
    default: return 'assisted';
  }
}

// ─── Model brand → UUID resolution (sysadmin scope) ─────────────────────────
//
// Audit C1 fix — resolve the Architect's `set_model` brand_id alias
// (e.g. `"argus_pro"`) to the matching argus_models UUID. The catalog
// API requires `modelId: uuid|null`; the architect tool schema emits
// `brand: z.string()`. Mismatch = every Architect-driven save 400s.

export interface ResolvableModel {
  id: string;
  brandId: string;
}

/**
 * Resolve a brand_id alias to an argus_models row. Pure / exported for tests.
 */
export function resolveModelByBrand<M extends ResolvableModel>(
  models: readonly M[],
  brand: string,
): M | null {
  if (!brand) return null;
  return models.find((m) => m.brandId === brand) ?? null;
}

/**
 * Build the partial config patch for a `set_model` mutation on sysadmin
 * scope. Pure / exported for tests.
 *
 * Returns `{ patch, error, loading }`:
 *   - patch: a Partial<BuilderConfig>-shaped object the caller should
 *     pass to setConfig. Null means "do not mutate".
 *   - error: a human-readable string when the resolution failed (used
 *     to surface a toast). Null on success.
 *   - loading: true when the models catalog query is still in flight
 *     and we cannot yet determine if the brand resolves. Caller should
 *     leave the mutation pending so the user can retry once loaded.
 *
 * Audit L1 follow-up — cold-page race: if the LLM emits `set_model`
 * before `useArgusModels()` resolves, `models` is `[]` and we would
 * mis-report "Unknown model brand" for a brand that IS in the
 * catalog. The `isLoading` guard returns `{ loading: true }` instead
 * so the caller can keep the mutation card pending without firing a
 * misleading toast.
 */
export function applySetModelMutation<M extends ResolvableModel>(
  models: readonly M[],
  args: { brand?: unknown },
  opts?: { isLoading?: boolean },
): {
  patch: { modelId: string; modelIds: string[] } | null;
  error: string | null;
  loading: boolean;
} {
  const brand = typeof args.brand === 'string' ? args.brand : '';
  if (!brand) {
    return {
      patch: null,
      error: 'Architect emitted set_model without a brand value',
      loading: false,
    };
  }
  // L1 — defer resolution until the catalog query resolves. We only
  // gate when isLoading is true AND the catalog is currently empty;
  // a non-empty catalog means we can resolve immediately even mid-
  // refetch.
  if (opts?.isLoading && models.length === 0) {
    return { patch: null, error: null, loading: true };
  }
  const match = resolveModelByBrand(models, brand);
  if (!match) {
    return {
      patch: null,
      error: `Architect proposed brand "${brand}" but it is not in the platform model catalog.`,
      loading: false,
    };
  }
  return {
    patch: { modelId: match.id, modelIds: [match.id] },
    error: null,
    loading: false,
  };
}
